import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDragLayer } from 'react-dnd';

import { type Clip, ClipType } from '@air/api/types';

import { ImageSearchZoneOverlay } from '~/components/PrivateDropzone/components/ImageSearchZoneOverlay';
import DragType, { type GetAssetDragItem } from '~/components/Shared/Drag/dragTypes';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { distanceToRect, pointWithinRect } from '~/utils/DragUtils';

const DEFAULT_PROXIMITY_THRESHOLD = 70;
const MAX_PROXIMITY_THRESHOLD = 150;

export type PrivateSearchCommandDropPayload = {
  item: Clip;
};

export type PrivateSearchCommandDragOverlayProps = {
  onDrop: (payload: PrivateSearchCommandDropPayload) => void;
};

export const PrivateSearchCommandDragOverlay = memo(({ onDrop }: PrivateSearchCommandDragOverlayProps) => {
  const { canUseSmartSearch } = usePrivateSearchContext();
  const [searchTriggerRect, setSearchTriggerRect] = useState<DOMRect | null>(null);
  const [privateBoardContentRect, setPrivateBoardContentRect] = useState<DOMRect | null>(null);
  const [dropZoneRect, setDropZoneRect] = useState<DOMRect | null>(null);
  const [showSearchByImageOverlay, setShowSearchByImageOverlay] = useState(false);
  const [hoveredZone, setHoveredZone] = useState<'search-by-image' | null>(null);

  const dropZoneSearchByImageRef = useRef<HTMLDivElement>(null);
  const lastDropCandidateRef = useRef<GetAssetDragItem | null>(null);
  const prevIsDraggingRef = useRef(false);

  // Use useDragLayer for consistent monitoring pattern
  const {
    itemType,
    isDragging: isCurrentlyDragging,
    item,
    clientOffset,
  } = useDragLayer((monitor) => ({
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
    item: monitor.getItem() as GetAssetDragItem | null,
    clientOffset: monitor.getClientOffset(),
  }));

  // Determine if this is a valid drag operation for search-by-image
  const isValidDrag =
    isCurrentlyDragging &&
    itemType === DragType.asset &&
    item?.firstItem.type === ClipType.photo &&
    item?.count === 1 &&
    canUseSmartSearch;

  const currentItem = isValidDrag ? item : null;
  const isDragging = isValidDrag;

  const updateRects = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const trigger = document.getElementById('SEARCH_COMMAND_TRIGGER');
    setSearchTriggerRect(trigger ? trigger.getBoundingClientRect() : null);

    const privateBoardContent = document.getElementById('PRIVATE_BOARD_CONTENT');
    setPrivateBoardContentRect(privateBoardContent ? privateBoardContent.getBoundingClientRect() : null);
  }, []);

  // Calculate the proximity threshold between the bottom of the search trigger and the top of the private board content
  // The reason for this is to avoid interfering with content area drag and drop operations
  const proximityThreshold = useMemo(() => {
    if (!searchTriggerRect || !privateBoardContentRect) {
      return DEFAULT_PROXIMITY_THRESHOLD;
    }

    return Math.max(
      DEFAULT_PROXIMITY_THRESHOLD,
      Math.min(Math.max(0, privateBoardContentRect.top - searchTriggerRect.bottom), MAX_PROXIMITY_THRESHOLD),
    );
  }, [searchTriggerRect, privateBoardContentRect]);

  useEffect(() => {
    if (isDragging) {
      updateRects();
    } else {
      setSearchTriggerRect(null);
      setShowSearchByImageOverlay(false);
      setHoveredZone(null);
    }
  }, [isDragging, updateRects]);

  const distance = useMemo(
    () => (!clientOffset || !searchTriggerRect ? Infinity : distanceToRect(clientOffset, searchTriggerRect)),
    [clientOffset, searchTriggerRect],
  );

  // Sticky behavior: once triggered by proximity, overlay stays visible until explicitly hidden
  useEffect(() => {
    if (isDragging && searchTriggerRect && distance <= proximityThreshold) {
      setShowSearchByImageOverlay(true);
    }
  }, [isDragging, searchTriggerRect, distance, proximityThreshold]);

  useEffect(() => {
    if (isDragging && showSearchByImageOverlay && dropZoneSearchByImageRef.current) {
      setDropZoneRect(dropZoneSearchByImageRef.current.getBoundingClientRect());
    } else {
      setDropZoneRect(null);
    }
  }, [isDragging, showSearchByImageOverlay]);

  useEffect(() => {
    if (!isDragging || !clientOffset || !dropZoneRect) {
      return;
    }

    const isOverDropZone = pointWithinRect(clientOffset, dropZoneRect);
    const newHoveredZone = isOverDropZone ? 'search-by-image' : null;

    setHoveredZone((prev) => (prev !== newHoveredZone ? newHoveredZone : prev));
  }, [isDragging, clientOffset, dropZoneRect]);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const isWithinProximity = distance <= proximityThreshold;
    if (hoveredZone !== 'search-by-image' && !isWithinProximity && showSearchByImageOverlay) {
      setShowSearchByImageOverlay(false);
    }
  }, [isDragging, hoveredZone, distance, showSearchByImageOverlay, proximityThreshold]);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    if (hoveredZone === 'search-by-image' && currentItem && dropZoneRect) {
      lastDropCandidateRef.current = currentItem;
    } else {
      lastDropCandidateRef.current = null;
    }
  }, [isDragging, currentItem, hoveredZone, dropZoneRect]);

  useEffect(() => {
    if (prevIsDraggingRef.current && !isDragging) {
      const item = lastDropCandidateRef.current;

      if (item) {
        onDrop({ item: item.firstItem });
      }

      lastDropCandidateRef.current = null;
    }

    prevIsDraggingRef.current = isDragging;
  }, [isDragging, onDrop]);

  if (!showSearchByImageOverlay || !searchTriggerRect) {
    return null;
  }

  return (
    <div data-testid="PRIVATE_SEARCH_COMMAND_DRAG_OVERLAY" className="fixed inset-0 z-30">
      {/* Prevents interaction with underlying UI during drag operations */}
      <div className="pointer-events-auto absolute inset-0" />

      <div className="pointer-events-none absolute inset-0">
        <ImageSearchZoneOverlay
          hoveredZone={hoveredZone}
          dropZoneSearchByImageRef={dropZoneSearchByImageRef}
          showSearchByImageOverlay={showSearchByImageOverlay}
          searchCommandTriggerPosition={searchTriggerRect}
          dropzoneDescription="Search your workspace for images visually similar to this asset."
        />
      </div>
    </div>
  );
});

PrivateSearchCommandDragOverlay.displayName = 'PrivateSearchCommandDragOverlay';
